/**
 * @file: useDeliveryCheckMediaController.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { useAppNavigation } from '@euler/app/components/AppNavigationProvider';
import { useCurrentUserDangerously } from '@euler/app/flows/auth/context';
import { useMediaCaptureWithAnnotation } from '@euler/app/flows/media/functions/useMediaCaptureWithAnnotation';
import { TaskImageWatermark } from '@euler/app/flows/task/components/ImageWatermark';
import { MediaInfo } from '@euler/app/flows/task/functions/models';
import {
  MediaUploadTask,
  MediaUploadTaskEvent,
} from '@euler/app/flows/task/functions/task-detail/MediaFileUploadController';
import { TaskFileUploadRequestContext } from '@euler/app/flows/task/functions/task-detail/types';
import { mediaStagedToMediaInfo } from '@euler/app/flows/task/functions/task-detail/util';
import { useTaskContext } from '@euler/app/flows/task/functions/TaskContext';
import {
  DeliveryCheckSceneType,
  ImageAnnotationScene,
} from '@euler/model/enum';
import { TaskMediaStaged } from '@euler/model/task-detail';
import { ReworkRecordStaged } from '@euler/model/task-detail/ReworkRecord';
import { withConfirmation } from '@euler/utils';
import {
  useBehaviorSubject,
  useCreation,
  usePersistFn,
} from '@euler/utils/hooks';
import produce, { Immutable } from 'immer';
import { useCallback, useEffect, useMemo } from 'react';
import { BehaviorSubject } from 'rxjs';

export const useDeliveryCheckReworkRecordMediaController = (options: {
  recordInfo: ReworkRecordStaged;
}) => {
  const { recordInfo } = options;
  const { sceneType } = recordInfo;
  const appNavigation = useAppNavigation();
  const profile = useCurrentUserDangerously();
  const takeMedia = useMediaCaptureWithAnnotation({
    scene: ImageAnnotationScene.ReworkRecord,
  });

  const { taskManager } = useTaskContext();

  const mediasStaged$ = useCreation(
    () => new BehaviorSubject<TaskMediaStaged[]>(recordInfo.medias),
    [recordInfo.medias],
  );

  const [mediasStaged, setMediasStaged] = useBehaviorSubject(mediasStaged$);

  const medias = useMemo(
    () => mediasStaged.map(mediaStagedToMediaInfo),
    [mediasStaged],
  );

  const onFileUploadEvent = usePersistFn(
    (
      e: MediaUploadTaskEvent,
      task: Immutable<MediaUploadTask<TaskFileUploadRequestContext>>,
    ) => {
      const request = task.request;
      const context = request.context;

      // ignore unwanted event that is not specific to me.
      if (context == null || context.type !== 'rework-record-media') {
        return;
      }

      if (e.type === 'queued') {
        // add a new media state when queued.
        const updated = produce(mediasStaged, draft => {
          if (context.mediaKey) {
            const index = draft.findIndex(x => x._key === context.mediaKey);
            if (index >= 0) {
              const media = draft[index];
              media.annotationMetadata = context.annotationMetadata as any;
              media._source = {
                type: 'local',
                localFileUri: request.localFileUri,
                contentType: request.contentType,
                uploadParameters: request.parameters,
                state: { status: 'queued' },
              };
              return;
            }
          }

          draft.push({
            type: request.contentType,
            annotationMetadata: context.annotationMetadata as any,
            _key: context.mediaKey ?? task.key,
            _timestamp: Date.now(),
            _source: {
              type: 'local',
              localFileUri: request.localFileUri,
              contentType: request.contentType,
              uploadParameters: request.parameters,
              state: { status: 'queued' },
            },
          });
        });
        mediasStaged$.next(updated);
      } else {
        // update the existing media state.
        const updated = produce(mediasStaged$.getValue(), draft => {
          // use original item to speed up searching
          const index = draft.findIndex(
            x => x._key === (context.mediaKey ?? task.key),
          );
          if (index < 0) {
            return;
          }
          const media = draft[index];
          if (media._source.type !== 'local') return;
          switch (e.type) {
            case 'compressing': {
              media._source.state = {
                status: 'compressing',
                progress: e.progress,
              };
              return;
            }
            case 'begin': {
              media._source.state = { status: 'uploading', progress: 0 };
              return;
            }
            case 'progress': {
              media._source.state = {
                status: 'uploading',
                progress: e.progress,
              };
              return;
            }
            case 'error': {
              media._source.state = { status: 'error', error: e.error };
              return;
            }
            case 'abort': {
              if (task.status !== 'aborted') {
                media._source.state = {
                  status: 'error',
                  error: e.error ?? new Error('File uploading has aborted'),
                };
              }
              return;
            }
            case 'finished': {
              const cover = e.result.extra?.cover;
              media.coverUrl =
                cover != null && cover !== '' ? cover : undefined;
              media._source = {
                type: 'remote',
                url: e.result.url,
                cid: e.result.name,
              };
              return;
            }
            default:
              return;
          }
        });
        mediasStaged$.next(updated);
      }
    },
  );

  useEffect(() => {
    const removeListener =
      taskManager.uploadController.addListener(onFileUploadEvent);
    return () => {
      removeListener();
    };
  }, [onFileUploadEvent, taskManager.uploadController]);

  const onTakeMedia = usePersistFn(() => {
    takeMedia({
      tags: () => [],
      defaultTag: () => ({
        name: '内返记录',
        color: 'warning',
      }),
      watermark: (scale?: number) => (
        <TaskImageWatermark
          licensePlateNo={recordInfo.licensePlateNo}
          title={recordInfo.checkTitle}
          author={profile.user.name}
          store={profile.store.name}
          type={
            sceneType === DeliveryCheckSceneType.CompletionCheck
              ? 'delivery-check'
              : 'sampling-check'
          }
          scale={scale}
        />
      ),
      uploadVideoFile: async (localFileUri, contentType, rotate) => {
        taskManager.uploadController.enqueue({
          localFileUri,
          contentType,
          type: 'video',
          context: {
            type: 'rework-record-media',
          },
          parameters: {
            cover: true,
            blurhash: true,
            videoRotate: rotate,
          },
        });
      },
      handleImageAnnotationResults: async results => {
        for (const result of results) {
          taskManager.uploadController.enqueue({
            localFileUri: result.imageUri,
            contentType: 'image/jpeg',
            type: 'image',
            parameters: {
              cover: true,
              blurhash: true,
            },
            context: {
              type: 'rework-record-media',
              annotationMetadata: result.annotationMetadata,
            },
          });
        }
      },
    });
  });

  const onDeleteMedia = useCallback(
    async (media: MediaInfo) => {
      const label = media.type === 'image' ? '图片' : '视频';
      const confirmed = await withConfirmation({
        title: '确认',
        message: `是否确认删除该${label}?`,
        confirmButtonText: '是',
        cancelButtonText: '否',
      });
      if (confirmed) {
        setMediasStaged(prev => prev.filter(x => x._key !== media.key));
      }
    },
    [setMediasStaged],
  );

  const onRetryMediaUpload = useCallback(
    (media: MediaInfo) => {
      if (media.source?.type !== 'local') return;
      taskManager.uploadController.enqueue({
        localFileUri: media.source.localFileUri,
        contentType: media.source.contentType,
        type: media.type,
        parameters: media.source.uploadParameters,
        context: {
          type: 'rework-record-media',
          mediaKey: media.key,
        },
      });
    },
    [taskManager.uploadController],
  );

  const onPreviewVideo = useCallback(
    (media: MediaInfo) => {
      const stagedMedia = mediasStaged.find(x => x._key === media.key);
      if (
        stagedMedia &&
        stagedMedia._source.type === 'local' &&
        stagedMedia._source.state.status === 'uploaded'
      ) {
        appNavigation.navigate('_mediaPreview', {
          type: 'video',
          url: stagedMedia._source.localFileUri,
        });
      } else {
        appNavigation.navigate('_mediaPreview', {
          type: 'video',
          url: media.url,
        });
      }
    },
    [appNavigation, mediasStaged],
  );

  return {
    medias,
    mediasStaged,
    onTakeMedia,
    onDeleteMedia,
    onRetryMediaUpload,
    onPreviewVideo,
  };
};
